import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Monitor, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Code,
  Cloud,
  Database,
  TrendingUp,
  Target,
  Server,
  Smartphone
} from 'lucide-react';

export default function MarchesPublicsInformatiquePage() {
  return (
    <>
      <SEO 
        title="Marchés Publics Informatique : Appels d'Offres IT et Services Numériques"
        description="Comment trouver des marchés publics en informatique et services numériques. Guide complet : appels d'offres IT, développement logiciel, infogérance, cloud. Conseils pour ESN et entreprises tech."
        path="/marches-publics-informatique"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics informatique' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 py-16 text-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-300 rounded-full text-sm font-medium mb-6">
                <Monitor className="w-4 h-4" />
                Secteur IT & Services Numériques
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Marchés Publics Informatique : Trouver des Appels d'Offres IT et Services Numériques
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                La transformation numérique du secteur public génère des milliers d'opportunités pour les ESN, 
                éditeurs de logiciels et prestataires IT. Découvrez comment identifier et remporter ces marchés.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-violet-600 hover:bg-violet-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés IT
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '15 Md€', label: 'dépenses IT publiques/an', icon: TrendingUp },
                { value: '+20%', label: 'croissance annuelle', icon: Target },
                { value: '5000+', label: 'appels d\'offres IT/an', icon: FileText },
                { value: '60%', label: 'attribués aux PME', icon: Code }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-violet-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Monitor className="w-8 h-8 text-violet-600" />
                Quels types de marchés publics en informatique ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les <strong>marchés publics informatiques</strong> couvrent l'ensemble des besoins numériques 
                  des administrations. Voici les principales catégories :
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                {[
                  { title: 'Développement logiciel', desc: 'Applications métiers, sites web, portails citoyens, API', icon: Code },
                  { title: 'Infogérance & support', desc: 'TMA, helpdesk, maintenance applicative, support utilisateurs', icon: Server },
                  { title: 'Cloud & hébergement', desc: 'Infrastructure cloud, hébergement sécurisé, PaaS/IaaS', icon: Cloud },
                  { title: 'Fourniture matériel', desc: 'Postes de travail, serveurs, équipements réseau', icon: Monitor },
                  { title: 'Cybersécurité', desc: 'Audits, SOC, pentests, RSSI externalisé', icon: Database },
                  { title: 'Mobilité & apps', desc: 'Applications mobiles, solutions de mobilité, BYOD', icon: Smartphone }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-violet-50 rounded-xl p-5 border border-violet-100">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-violet-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-violet-50 border-l-4 border-violet-500 p-4 mt-6 rounded-r-lg">
                <p className="text-violet-800">
                  <strong>Tendance 2026 :</strong> Les marchés liés à l'IA, à la dématérialisation et à la 
                  conformité RGPD sont en forte croissance. La doctrine "Cloud au centre" de l'État favorise 
                  également les solutions cloud souveraines.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Search className="w-8 h-8 text-violet-600" />
                Où trouver les appels d'offres IT ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les marchés informatiques sont publiés sur les plateformes classiques, mais certains acheteurs 
                  majeurs méritent une attention particulière :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { 
                    name: 'UGAP', 
                    desc: 'Centrale d\'achat public proposant des marchés informatiques prénégociés',
                    detail: 'Référencement sur catalogue possible'
                  },
                  { 
                    name: 'Ministères (DINUM)', 
                    desc: 'Direction interministérielle du numérique, pilote les grands projets IT de l\'État',
                    detail: 'Marchés stratégiques de transformation'
                  },
                  { 
                    name: 'Collectivités territoriales', 
                    desc: 'Régions, départements, métropoles - besoins variés en IT',
                    detail: 'Portails citoyens, GRC, SIG...'
                  },
                  { 
                    name: 'Établissements de santé', 
                    desc: 'Hôpitaux, ARS - secteur très porteur en IT santé',
                    detail: 'DPI, télémédecine, interopérabilité'
                  },
                  { 
                    name: 'Éducation nationale', 
                    desc: 'Académies, universités, CROUS',
                    detail: 'ENT, outils pédagogiques, gestion'
                  }
                ].map((source, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-violet-300 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900">{source.name}</h3>
                      <p className="text-slate-600 text-sm">{source.desc}</p>
                      <span className="text-xs text-violet-600 font-medium">{source.detail}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-2xl p-6 mt-8">
                <h3 className="text-xl font-bold mb-3">Automatisez votre veille IT</h3>
                <p className="text-violet-100 mb-4">
                  Avec notre <Link to="/veille-marches-publics" className="underline font-medium text-white">veille marchés publics</Link>, 
                  recevez automatiquement les appels d'offres correspondant à vos compétences techniques : 
                  développement, cloud, cybersécurité, infogérance...
                </p>
                <Link to="/">
                  <Button variant="secondary" className="bg-white text-violet-600 hover:bg-violet-50">
                    Configurer ma veille IT
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-violet-600" />
                Comment analyser un DCE informatique ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les DCE de marchés IT sont souvent techniques et volumineux. 
                  Voici les éléments clés à identifier rapidement :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { step: '1', title: 'Périmètre fonctionnel', desc: 'Fonctionnalités attendues, cas d\'usage, user stories' },
                  { step: '2', title: 'Stack technique', desc: 'Technologies imposées ou recommandées, contraintes d\'interopérabilité' },
                  { step: '3', title: 'Volumétrie & SLA', desc: 'Nombre d\'utilisateurs, temps de réponse, disponibilité attendue' },
                  { step: '4', title: 'Sécurité & conformité', desc: 'Exigences RGPD, RGS, hébergement HDS si données de santé' },
                  { step: '5', title: 'Modalités de réalisation', desc: 'Agile/cycle en V, jalons, livrables, réversibilité' },
                  { step: '6', title: 'Profils demandés', desc: 'Certifications attendues, expérience sur projets similaires' }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1 bg-slate-50 rounded-xl p-4">
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-slate-600 mt-6">
                Notre <Link to="/analyse-dce" className="text-violet-600 font-medium hover:underline">outil d'analyse DCE par IA</Link> extrait 
                automatiquement les spécifications techniques et fonctionnelles en quelques secondes.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-violet-600" />
                Les erreurs fréquentes des ESN et prestataires IT
              </h2>
              
              <div className="space-y-4">
                {[
                  { 
                    error: 'Proposer une solution surdimensionnée',
                    solution: 'Adaptez votre offre au besoin réel. Une collectivité moyenne n\'a pas les mêmes besoins qu\'un ministère.'
                  },
                  { 
                    error: 'Négliger les références similaires',
                    solution: 'Les acheteurs IT valorisent l\'expérience sur des projets comparables. Documentez vos références.'
                  },
                  { 
                    error: 'Sous-estimer la réversibilité',
                    solution: 'Prévoyez dès le départ les conditions de sortie : documentation, transfert de compétences, données.'
                  },
                  { 
                    error: 'Ignorer les contraintes de sécurité',
                    solution: 'RGS, RGPD, HDS... Vérifiez les exigences de sécurité et conformité avant de répondre.'
                  },
                  { 
                    error: 'Proposer un planning irréaliste',
                    solution: 'Un planning trop optimiste sera questionné. Soyez réaliste sur les délais de développement.'
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold text-sm">✗</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.error}</h3>
                        <div className="flex items-start gap-2 mt-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                          <p className="text-slate-600 text-sm">{item.solution}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-violet-600" />
                Optimiser sa veille marchés IT
              </h2>
              
              <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Les clés d'une veille efficace</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Mots-clés techniques</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Développement web</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Infogérance</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Cloud computing</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Cybersécurité</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> TMA / maintenance</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Codes CPV informatique</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 72000000 - Services IT</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 72200000 - Développement logiciel</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 72300000 - Services de données</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 72400000 - Services Internet</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 48000000 - Logiciels</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-violet-900 to-indigo-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez vos prochains marchés IT
            </h2>
            <p className="text-violet-200 mb-8 max-w-xl mx-auto">
              Recevez gratuitement 3 recommandations de marchés publics 
              correspondant à vos compétences techniques.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-violet-50">
                <Code className="w-5 h-5 mr-2" />
                Commencer ma veille IT gratuite
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="informatique" />

        <Footer />
      </div>
    </>
  );
}
